import { useEffect, useState } from "react";
import { Gift, Printer } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { Logo } from "./Logo";
import { PHONE_DISPLAY } from "@/lib/contact";

export const ReferralsPanel = () => {
  const { t } = useLang();
  const r = t.dash.referrals;
  const q = t.dash.qr;
  const [rows, setRows] = useState(null);
  const [refName, setRefName] = useState("");

  const qrUrl = `${window.location.origin}/${refName.trim() ? `?ref=${encodeURIComponent(refName.trim())}` : ""}`;

  useEffect(() => {
    api.get("/referrals").then((res) => setRows(res.data));
  }, []);

  return (
    <div className="space-y-6" data-testid="referrals-panel">
      <p className="text-sm text-white/40">{r.subtitle}</p>
      <div className="border border-white/10 bg-[#111111] overflow-x-auto">
        <table className="w-full text-sm" data-testid="referrals-table">
          <thead>
            <tr className="border-b border-white/10 text-left text-xs uppercase tracking-widest text-white/40">
              <th className="px-5 py-4 font-medium">{r.cols.referrer}</th>
              <th className="px-5 py-4 font-medium">{r.cols.total}</th>
              <th className="px-5 py-4 font-medium">{r.cols.converted}</th>
              <th className="px-5 py-4 font-medium">{r.cols.discount}</th>
            </tr>
          </thead>
          <tbody>
            {rows && rows.length === 0 && (
              <tr>
                <td colSpan={4} className="px-5 py-16 text-center text-white/40" data-testid="referrals-empty">
                  {r.empty}
                </td>
              </tr>
            )}
            {rows &&
              rows.map((row) => (
                <tr key={row.referrer} className="border-b border-white/5" data-testid={`referral-row-${row.referrer}`}>
                  <td className="px-5 py-4 font-semibold">{row.referrer}</td>
                  <td className="px-5 py-4 text-white/70">{row.total}</td>
                  <td className="px-5 py-4 text-emerald-400 font-semibold">{row.converted}</td>
                  <td className="px-5 py-4">
                    <span
                      className={`inline-flex items-center gap-2 border px-3 py-1 rounded-full text-xs font-semibold ${
                        row.discount >= 100
                          ? "border-[#FF7900] bg-[#FF7900]/10 text-[#FF7900]"
                          : row.discount > 0
                          ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-400"
                          : "border-white/10 text-white/40"
                      }`}
                    >
                      {row.discount >= 100 && <Gift className="w-3.5 h-3.5" />}
                      -{row.discount}%
                    </span>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <div className="border border-white/10 bg-[#111111] p-6 md:p-8" data-testid="qr-card-generator">
        <h3 className="font-display font-semibold mb-1">{q.title}</h3>
        <p className="text-sm text-white/40 mb-6">{q.subtitle}</p>
        <div className="flex flex-col lg:flex-row gap-8 lg:items-start">
          <div className="flex-1 space-y-4">
            <input
              value={refName}
              onChange={(e) => setRefName(e.target.value)}
              placeholder={q.refName}
              data-testid="qr-ref-input"
              className="w-full bg-black/40 border border-white/15 px-3 py-2 text-sm outline-none focus:border-[#FF7900] transition-colors duration-300"
            />
            <p className="text-xs text-white/30 break-all">{qrUrl}</p>
            <button
              onClick={() => window.print()}
              data-testid="qr-print-button"
              className="inline-flex items-center gap-2 bg-[#FF7900] hover:bg-white hover:text-black text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-colors duration-300"
            >
              <Printer className="w-4 h-4" /> {q.print}
            </button>
          </div>
          <div className="print-card flex flex-col gap-4 shrink-0">
            <div className="bg-white text-black rounded-xl p-6 w-[340px] shadow-2xl" data-testid="qr-card-preview">
              <div className="flex items-center gap-2.5 mb-4">
                <Logo size={30} />
                <p className="font-display font-semibold text-sm leading-tight">
                  Smart <span className="text-[#FF7900]">Telecom</span> Advisor
                </p>
              </div>
              <div className="flex items-center gap-5">
                <div className="bg-white p-1.5 border border-black/10 rounded-lg">
                  <QRCodeSVG value={qrUrl} size={110} fgColor="#000000" bgColor="#FFFFFF" />
                </div>
                <div className="min-w-0">
                  <p className="text-[11px] uppercase tracking-widest text-[#FF7900] font-semibold mb-1">{q.scan}</p>
                  <p className="text-xs text-black/60 leading-relaxed">Orange Belgium · Eneco</p>
                  <p className="text-xs font-semibold mt-2">{PHONE_DISPLAY}</p>
                  {refName.trim() && <p className="text-[11px] text-black/50 mt-1 truncate">Ref: {refName.trim()}</p>}
                </div>
              </div>
            </div>
            <div className="bg-white text-black rounded-xl p-6 w-[340px] shadow-2xl" data-testid="qr-card-back">
              <ul className="flex flex-col gap-2 mb-4">
                {q.back.services.map((s) => (
                  <li key={s} className="flex items-center gap-2.5 text-xs font-medium">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#FF7900] shrink-0" />
                    {s}
                  </li>
                ))}
              </ul>
              <div className="border-t border-black/10 pt-3">
                <p className="text-[11px] uppercase tracking-widest text-[#FF7900] font-semibold mb-1.5">{q.back.offerTitle}</p>
                <p className="text-[11px] text-black/65 leading-relaxed">{q.back.offer}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
